import { useMemo } from 'react';
import { useEventDocuments } from '@api/bff/eventDocuments/queries/useEventDocuments';
import type { EventDocumentViewModel } from '@api/bff/openapi/models';
import type { PromotedPhoto } from '../types';

type FullPhotoData = {
  id: string;
  fullDataUrl: string;
  caption: string | null;
  isUnsynced?: boolean;
};

type UseEquipmentPhotosWithFullContentParams = {
  eventNum: number | null;
  coverPhotoIndex: number | null;
  promotedPhotoIndices: number[];
  promotedPhotos: PromotedPhoto[];
  fallbackCoverUrl: string | null;
  coverPhotoIsUnsynced?: boolean;
};

type UseEquipmentPhotosWithFullContentResult = {
  coverPhoto: string | null;
  coverPhotoIsUnsynced: boolean;
  promotedPhotos: FullPhotoData[];
  isLoading: boolean;
  hasFetchedFullPhotos: boolean;
};

const matchPhotoByIndex = (
  photoIndex: number,
  eventDocuments: EventDocumentViewModel[]
): EventDocumentViewModel | null => {
  const expectedDocNumber = `PHOTO-${String(photoIndex + 1).padStart(3, '0')}`;
  return (
    eventDocuments.find(doc => doc.documentNumber === expectedDocNumber) ?? null
  );
};

const base64ToDataUrl = (base64: string, mimeType = 'image/png'): string => {
  if (base64.startsWith('data:')) {
    return base64;
  }
  return `data:${mimeType};base64,${base64}`;
};

export const useEquipmentPhotosWithFullContent = ({
  eventNum,
  coverPhotoIndex,
  promotedPhotoIndices,
  promotedPhotos,
  fallbackCoverUrl,
  coverPhotoIsUnsynced = false
}: UseEquipmentPhotosWithFullContentParams): UseEquipmentPhotosWithFullContentResult => {
  const { data: eventDocuments, isLoading } = useEventDocuments(
    eventNum,
    eventNum != null && eventNum > 0
  );

  const result = useMemo(() => {
    if (!eventDocuments || eventDocuments.length === 0) {
      return {
        coverPhoto: fallbackCoverUrl,
        coverPhotoIsUnsynced,
        promotedPhotos: promotedPhotos.map(p => ({
          id: p.id,
          fullDataUrl: p.thumbnailDataUrl,
          caption: p.caption,
          isUnsynced: p.isUnsynced
        })),
        hasFetchedFullPhotos: false
      };
    }

    let coverPhoto: string | null = fallbackCoverUrl;
    if (coverPhotoIndex !== null) {
      const coverDoc = matchPhotoByIndex(coverPhotoIndex, eventDocuments);
      if (coverDoc?.fileContent) {
        coverPhoto = base64ToDataUrl(coverDoc.fileContent);
      }
    } else if (!fallbackCoverUrl) {
      // No photo metadata available (e.g. offline-completed calibration where
      // jsonData.photos was never populated). Fall back to the first PHOTO-*
      // event document so the cover photo still renders on the review page.
      const firstPhotoDoc = eventDocuments.find(
        doc => doc.documentNumber?.startsWith('PHOTO-') && doc.fileContent
      );
      if (firstPhotoDoc?.fileContent) {
        coverPhoto = base64ToDataUrl(firstPhotoDoc.fileContent);
      }
    }

    const fullPromotedPhotos: FullPhotoData[] = promotedPhotoIndices.map(
      (photoIndex, i) => {
        const promotedPhoto = promotedPhotos[i];
        const doc = matchPhotoByIndex(photoIndex, eventDocuments);

        if (doc?.fileContent) {
          return {
            id: promotedPhoto?.id ?? `promoted-${photoIndex}`,
            fullDataUrl: base64ToDataUrl(doc.fileContent),
            caption: promotedPhoto?.caption ?? null,
            isUnsynced: promotedPhoto?.isUnsynced
          };
        }

        return {
          id: promotedPhoto?.id ?? `promoted-${photoIndex}`,
          fullDataUrl: promotedPhoto?.thumbnailDataUrl ?? '',
          caption: promotedPhoto?.caption ?? null,
          isUnsynced: promotedPhoto?.isUnsynced
        };
      }
    );

    return {
      coverPhoto,
      coverPhotoIsUnsynced,
      promotedPhotos: fullPromotedPhotos,
      hasFetchedFullPhotos: true
    };
  }, [
    eventDocuments,
    coverPhotoIndex,
    promotedPhotoIndices,
    promotedPhotos,
    fallbackCoverUrl,
    coverPhotoIsUnsynced
  ]);

  return {
    ...result,
    isLoading
  };
};
